import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  History as HistoryIcon,
  AccountBalanceWallet as WalletIcon,
  CreditCard as CreditCardIcon,
  AssignmentTurnedIn as AssignIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { feeAPI, classAPI, studentAPI, academicYearAPI } from '../services/api';

// Register ChartJS components
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const Fees = () => {
  const [activeTab, setActiveTab] = useState(0); // 0: Dashboard, 1: Collection, 2: Setup
  const [loading, setLoading] = useState(false);
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeStructures, setFeeStructures] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [summaryData, setSummaryData] = useState(null);

  // Dialog States
  const [openHeadDialog, setOpenHeadDialog] = useState(false);
  const [openStructureDialog, setOpenStructureDialog] = useState(false);

  // Form States
  const [headForm, setHeadForm] = useState({ name: '', description: '', type: 'recurring' });
  const [structureForm, setStructureForm] = useState({
    feeHeadId: '', classId: '', amount: '', dueDate: '', academicYearId: ''
  });

  // Collection States
  const [searchStudent, setSearchStudent] = useState('');
  const [studentResults, setStudentResults] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentFees, setStudentFees] = useState([]);
  const [openPaymentDialog, setOpenPaymentDialog] = useState(false);
  const [selectedFee, setSelectedFee] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', paymentMode: 'cash', remarks: '' });

  // Assign Fee States
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [availableStructures, setAvailableStructures] = useState([]);
  const [selectedStructures, setSelectedStructures] = useState([]);

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [headsRes, clsRes, summaryRes, ayRes] = await Promise.all([
        feeAPI.getHeads(),
        classAPI.getAll(),
        feeAPI.getSummary(),
        academicYearAPI.getAll()
      ]);
      setFeeHeads(headsRes.data.data);
      setClasses(clsRes.data.data);
      setSummaryData(summaryRes.data.data);
      setAcademicYears(ayRes.data.data || []);

      // Set default academic year if active
      const activeAY = ayRes.data.data.find(ay => ay.status === 'active' || ay.isCurrent);
      if (activeAY) {
        setStructureForm(prev => ({ ...prev, academicYearId: activeAY.id }));
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateHead = async () => {
    try {
      await feeAPI.createHead(headForm);
      toast.success('Fee Head created');
      setOpenHeadDialog(false);
      const res = await feeAPI.getHeads();
      setFeeHeads(res.data.data);
    } catch (error) {
      toast.error('Failed to create fee head');
    }
  };

  const fetchStructures = async () => {
    setLoading(true);
    try {
      const res = await feeAPI.getStructures();
      setFeeStructures(res.data.data);
    } catch (error) {
      toast.error('Failed to fetch structures');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStructure = async () => {
    try {
      const payload = {
        ...structureForm,
        dueDate: structureForm.dueDate || null
      };
      await feeAPI.createStructure(payload);
      toast.success('Fee Structure created');
      setOpenStructureDialog(false);
      fetchStructures();
    } catch (error) {
      toast.error('Failed to create fee structure');
    }
  };

  // Student Search with debounce
  const handleSearchStudent = async (searchValue) => {
    if (!searchValue || searchValue.length < 2) {
      setStudentResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await studentAPI.getAll({ search: searchValue });
      const results = res.data.data || [];
      // Frontend filter for exact matching
      const term = searchValue.toLowerCase();
      const filtered = results.filter(u => {
        const firstName = u.user?.firstName?.toLowerCase() || '';
        const lastName = u.user?.lastName?.toLowerCase() || '';
        const admNo = u.admissionNumber?.toLowerCase() || '';
        return firstName.includes(term) || lastName.includes(term) || admNo.includes(term);
      });
      setStudentResults(filtered);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearchStudent(searchStudent);
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchStudent]);

  const handleSelectStudent = async (student) => {
    setSelectedStudent(student);
    setStudentResults([]); // Clear results
    setSearchStudent('');
    fetchStudentFees(student.id);
  };

  const fetchStudentFees = async (studentId) => {
    setLoading(true);
    try {
      const res = await feeAPI.getStudentFees(studentId);
      setStudentFees(res.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch student fees');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPayment = (fee) => {
    setSelectedFee(fee);
    setPaymentForm({ amount: '', paymentMode: 'cash', remarks: '' });
    setOpenPaymentDialog(true);
  };

  // Assign Fees
  const handleOpenAssignDialog = async () => {
    if (!selectedStudent) return;
    setLoading(true);
    try {
      const res = await feeAPI.getStructures({ classId: selectedStudent.classId });
      const structures = res.data.data || [];
      // Filter out already assigned fee structures
      const assignedIds = studentFees.map(sf => sf.feeStructureId);
      const unassigned = structures.filter(s => !assignedIds.includes(s.id));
      setAvailableStructures(unassigned);
      setSelectedStructures(unassigned.map(s => s.id)); // select all by default
      setOpenAssignDialog(true);
    } catch (error) {
      toast.error('Failed to load fee structures');
    } finally {
      setLoading(false);
    }
  };

  const toggleStructureSelection = (structureId) => {
    setSelectedStructures(prev =>
      prev.includes(structureId)
        ? prev.filter(id => id !== structureId)
        : [...prev, structureId]
    );
  };

  const handleAssignFees = async () => {
    if (selectedStructures.length === 0) {
      toast.warning('Please select at least one fee structure');
      return;
    }
    setLoading(true);
    try {
      for (const feeStructureId of selectedStructures) {
        await feeAPI.assignFee({
          studentId: selectedStudent.id,
          feeStructureId,
          academicYearId: selectedStudent.academicYearId
        });
      }
      toast.success(`${selectedStructures.length} fee(s) assigned successfully`);
      setOpenAssignDialog(false);
      fetchStudentFees(selectedStudent.id);
    } catch (error) {
      toast.error('Failed to assign fees: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCollectFee = async () => {
    if (!selectedFee) return;
    try {
      await feeAPI.collectFee({
        studentFeeId: selectedFee.id,
        ...paymentForm
      });
      toast.success('Payment recorded successfully');
      setOpenPaymentDialog(false);
      fetchStudentFees(selectedStudent.id);
      // Refresh summary
      const summaryRes = await feeAPI.getSummary();
      setSummaryData(summaryRes.data.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Payment failed');
    }
  };

  // Render Dashboard
  const renderDashboard = () => {
    if (!summaryData) return <CircularProgress />;

    const pieData = {
      labels: ['Paid', 'Remaining'],
      datasets: [
        {
          data: [summaryData.totalPaid, summaryData.remaining],
          backgroundColor: ['#4caf50', '#f44336'],
          hoverBackgroundColor: ['#66bb6a', '#e57373'],
        },
      ],
    };

    // Placeholder data for Bar Chart (Mocking years or monthly trend if backend doesn't provide yet)
    // The screenshot has "Assigned Fees", "Paid Fees", "Remaining Fees" bars.
    const barData = {
      labels: ['Current Year'],
      datasets: [
        {
          label: 'Assigned Fees',
          data: [summaryData.totalAssigned],
          backgroundColor: 'rgba(255, 159, 64, 0.5)',
          borderColor: 'rgba(255, 159, 64, 1)',
          borderWidth: 1,
        },
        {
          label: 'Paid Fees',
          data: [summaryData.totalPaid],
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
        },
        {
          label: 'Remaining Fees',
          data: [summaryData.remaining],
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
        },
      ],
    };

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Fee Status (Overall)</Typography>
              <Box sx={{ height: 300, display: 'flex', justifyContent: 'center' }}>
                <Pie data={pieData} />
              </Box>
              <Typography align="center" variant="subtitle1" sx={{ mt: 2 }}>
                Remaining: ₹{summaryData.remaining?.toLocaleString()} ({summaryData.counts?.remainingPercentage}%)
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Fees & Scholarship Paid Summary</Typography>
              <Box sx={{ height: 300 }}>
                <Bar options={{ responsive: true, preserveAspectRatio: false }} data={barData} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  // Render Functions
  const renderSetupTab = () => (
    <Grid container spacing={3}>
      {/* Fee Heads Section */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Fee Heads</Typography>
            <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setOpenHeadDialog(true)}>
              Add New
            </Button>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Name</TableCell><TableCell>Type</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {feeHeads.map(head => (
                  <TableRow key={head.id}>
                    <TableCell>{head.name}</TableCell>
                    <TableCell><Chip label={head.type} size="small" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>

      {/* Fee Structures Section */}
      <Grid item xs={12} md={6}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Fee Structures</Typography>
            <Box>
              <IconButton onClick={fetchStructures} size="small"><RefreshIcon /></IconButton>
              <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setOpenStructureDialog(true)} sx={{ ml: 1 }}>
                Add New
              </Button>
            </Box>
          </Box>
          <TableContainer sx={{ maxHeight: 300 }}>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell>Class</TableCell><TableCell>Fee Head</TableCell><TableCell>Amount</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {feeStructures.map(struct => (
                  <TableRow key={struct.id}>
                    <TableCell>{struct.class?.name}</TableCell>
                    <TableCell>{struct.feeHead?.name}</TableCell>
                    <TableCell>{struct.amount}</TableCell>
                  </TableRow>
                ))}
                {feeStructures.length === 0 && <TableRow><TableCell colSpan={3} align="center">Click refresh to load</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderCollectionTab = () => (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search student by name or admission number (min 2 chars)..."
            value={searchStudent}
            onChange={(e) => setSearchStudent(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
            }}
          />
        </Box>

        {studentResults.length > 0 && (
          <TableContainer sx={{ mt: 2, border: '1px solid #eee' }}>
            <Table size="small">
              <TableBody>
                {studentResults.map(student => (
                  <TableRow key={student.id} hover onClick={() => handleSelectStudent(student)} sx={{ cursor: 'pointer' }}>
                    <TableCell>{student.user?.firstName} {student.user?.lastName}</TableCell>
                    <TableCell>{student.admissionNumber}</TableCell>
                    <TableCell>Class: {student.class?.name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {selectedStudent && (
        <Grid container spacing={3}>
          {/* Student Info */}
          <Grid item xs={12} md={4}>
            <Card sx={{ bgcolor: '#f5f5f5' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>{selectedStudent.user?.firstName} {selectedStudent.user?.lastName}</Typography>
                <Typography variant="body2">Admission No: {selectedStudent.admissionNumber}</Typography>
                <Typography variant="body2">Class: {selectedStudent.class?.name}</Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2">Guardian</Typography>
                <Typography variant="body2">{selectedStudent.fatherName || selectedStudent.motherName || 'N/A'}</Typography>
                <Typography variant="body2">{selectedStudent.fatherPhone || selectedStudent.motherPhone || 'N/A'}</Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Fee Records */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Fee Records</Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AssignIcon />}
                  onClick={handleOpenAssignDialog}
                  size="small"
                >
                  Assign Fees
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fee Type</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Paid</TableCell>
                      <TableCell align="right">Pending</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {studentFees.map(fee => {
                      const total = Number(fee.amount) || 0;
                      const paid = Number(fee.paidAmount) || 0;
                      const pending = total - paid;
                      return (
                        <TableRow key={fee.id}>
                          <TableCell>{fee.feeStructure?.feeHead?.name}</TableCell>
                          <TableCell align="right">₹{total.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ color: 'success.main', fontWeight: 600 }}>₹{paid.toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ color: pending > 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>₹{pending.toLocaleString()}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={fee.status}
                              color={fee.status === 'paid' ? 'success' : fee.status === 'partial' ? 'warning' : 'error'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {fee.status !== 'paid' && (
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<PaymentIcon />}
                                onClick={() => handleOpenPayment(fee)}
                              >
                                Pay
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {studentFees.length === 0 && (
                      <TableRow><TableCell colSpan={6} align="center">No fee records found</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Modern Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3,
        background: 'linear-gradient(135deg, #1a2332 0%, #2c3e50 60%, #1a9e8f 100%)',
        borderRadius: 3, p: 3, color: '#fff',
        position: 'relative', overflow: 'hidden',
        '&::before': {
          content: '""', position: 'absolute', top: -30, right: -30,
          width: 120, height: 120, borderRadius: '50%',
          background: 'rgba(255,255,255,0.06)',
        }
      }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#fff !important', letterSpacing: -0.3 }}>
            Fee Management
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6) !important', mt: 0.5 }}>
            Manage fee collection, structures, and financial reports
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ mb: 3, borderRadius: '12px !important' }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTab-root': {
              minHeight: 48,
              fontWeight: 600,
              '&.Mui-selected': { color: '#1a9e8f !important' }
            },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' }
          }}
        >
          <Tab label="Dashboard" icon={<PaymentIcon />} iconPosition="start" />
          <Tab label="Fee Collection" icon={<PaymentIcon />} iconPosition="start" />
          <Tab label="Setup & Structure" icon={<ReceiptIcon />} iconPosition="start" />
        </Tabs>
      </Paper>

      {activeTab === 0 && renderDashboard()}
      {activeTab === 1 && renderCollectionTab()}
      {activeTab === 2 && renderSetupTab()}

      {/* Create Fee Head Dialog */}
      <Dialog open={openHeadDialog} onClose={() => setOpenHeadDialog(false)}>
        <DialogTitle>Add Fee Head</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Name" margin="normal"
            value={headForm.name} onChange={e => setHeadForm({ ...headForm, name: e.target.value })}
          />
          <TextField
            fullWidth label="Description" margin="normal"
            value={headForm.description} onChange={e => setHeadForm({ ...headForm, description: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select value={headForm.type} onChange={e => setHeadForm({ ...headForm, type: e.target.value })}>
              <MenuItem value="recurring">Recurring</MenuItem>
              <MenuItem value="one-time">One-time</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHeadDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateHead}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Create Fee Structure Dialog */}
      <Dialog open={openStructureDialog} onClose={() => setOpenStructureDialog(false)}>
        <DialogTitle>Add Fee Structure</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Class</InputLabel>
            <Select value={structureForm.classId} onChange={e => setStructureForm({ ...structureForm, classId: e.target.value })}>
              {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Academic Year</InputLabel>
            <Select
              value={structureForm.academicYearId}
              onChange={e => setStructureForm({ ...structureForm, academicYearId: e.target.value })}
            >
              {academicYears.map(ay => <MenuItem key={ay.id} value={ay.id}>{ay.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Fee Head</InputLabel>
            <Select value={structureForm.feeHeadId} onChange={e => setStructureForm({ ...structureForm, feeHeadId: e.target.value })}>
              {feeHeads.map(h => <MenuItem key={h.id} value={h.id}>{h.name}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            fullWidth label="Amount" type="number" margin="normal"
            value={structureForm.amount} onChange={e => setStructureForm({ ...structureForm, amount: e.target.value })}
          />
          <TextField
            fullWidth label="Due Date" type="date" margin="normal" InputLabelProps={{ shrink: true }}
            value={structureForm.dueDate} onChange={e => setStructureForm({ ...structureForm, dueDate: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenStructureDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateStructure}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Collect Fee Dialog */}
      <Dialog open={openPaymentDialog} onClose={() => setOpenPaymentDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Collect Payment</DialogTitle>
        <DialogContent>
          {/* Fee Breakdown */}
          {selectedFee && (() => {
            const total = Number(selectedFee.amount) || 0;
            const alreadyPaid = Number(selectedFee.paidAmount) || 0;
            const pendingAmount = total - alreadyPaid;
            const enteringAmount = Number(paymentForm.amount) || 0;
            const afterPayment = pendingAmount - enteringAmount;
            return (
              <>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {selectedFee.feeStructure?.feeHead?.name}
                </Typography>
                <Box sx={{ bgcolor: '#f5f5f5', borderRadius: 1, p: 2, mb: 2 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6" fontWeight={600}>₹{total.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Already Paid</Typography>
                      <Typography variant="h6" fontWeight={600} color="success.main">₹{alreadyPaid.toLocaleString()}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" color="text.secondary">Pending</Typography>
                      <Typography variant="h6" fontWeight={600} color="error.main">₹{pendingAmount.toLocaleString()}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                {/* Payment Mode Selection */}
                <FormControl fullWidth margin="normal">
                  <InputLabel>Payment Mode</InputLabel>
                  <Select value={paymentForm.paymentMode} onChange={e => setPaymentForm({ ...paymentForm, paymentMode: e.target.value })}>
                    <MenuItem value="cash">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <WalletIcon fontSize="small" /> Cash (Manual Collection)
                      </Box>
                    </MenuItem>
                    <MenuItem value="card">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCardIcon fontSize="small" /> Card (Payment Gateway)
                      </Box>
                    </MenuItem>
                    <MenuItem value="online">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon fontSize="small" /> Online / Net Banking (Payment Gateway)
                      </Box>
                    </MenuItem>
                    <MenuItem value="upi">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PaymentIcon fontSize="small" /> UPI (Payment Gateway)
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Cash Mode — Manual Collection */}
                {paymentForm.paymentMode === 'cash' ? (
                  <>
                    <TextField
                      fullWidth
                      label="Amount to Collect"
                      type="number"
                      margin="normal"
                      value={paymentForm.amount}
                      onChange={e => {
                        const val = e.target.value;
                        if (val === '' || Number(val) <= pendingAmount) {
                          setPaymentForm({ ...paymentForm, amount: val });
                        }
                      }}
                      helperText={enteringAmount > 0
                        ? `After this payment: Pending ₹${afterPayment.toLocaleString()}`
                        : `Enter amount to pay (max ₹${pendingAmount.toLocaleString()})`}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>
                      }}
                      error={enteringAmount > pendingAmount}
                    />
                    <TextField
                      fullWidth label="Remarks / Receipt No." margin="normal"
                      value={paymentForm.remarks} onChange={e => setPaymentForm({ ...paymentForm, remarks: e.target.value })}
                    />
                  </>
                ) : (
                  /* Non-Cash Mode — Payment Gateway */
                  <Box sx={{ bgcolor: '#e3f2fd', borderRadius: 1, p: 2, mt: 2, textAlign: 'center' }}>
                    <CreditCardIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Payment Gateway
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {paymentForm.paymentMode === 'card' && 'Card payments'}
                      {paymentForm.paymentMode === 'online' && 'Online / Net Banking payments'}
                      {paymentForm.paymentMode === 'upi' && 'UPI payments'}
                      {' '}will be processed through the payment gateway.
                      This feature will be integrated with your school's payment provider.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      For now, please use <strong>Cash</strong> mode for manual collection.
                    </Typography>
                  </Box>
                )}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPaymentDialog(false)}>Cancel</Button>
          {paymentForm.paymentMode === 'cash' && (
            <Button
              variant="contained"
              color="success"
              onClick={handleCollectFee}
              disabled={!paymentForm.amount || Number(paymentForm.amount) <= 0 || Number(paymentForm.amount) > (Number(selectedFee?.amount) - Number(selectedFee?.paidAmount))}
            >
              Collect ₹{Number(paymentForm.amount || 0).toLocaleString()}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Assign Fee Dialog */}
      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Fee Structures to {selectedStudent?.user?.firstName} {selectedStudent?.user?.lastName}</DialogTitle>
        <DialogContent>
          {availableStructures.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No unassigned fee structures found for {selectedStudent?.class?.name}.
              Please create fee structures in "Setup & Structure" tab first.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select fee structures to assign to this student:
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (selectedStructures.length === availableStructures.length) {
                              setSelectedStructures([]);
                            } else {
                              setSelectedStructures(availableStructures.map(s => s.id));
                            }
                          }}
                        >
                          {selectedStructures.length === availableStructures.length
                            ? <CheckBoxIcon color="primary" />
                            : <CheckBoxOutlineBlankIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>Fee Head</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Due Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {availableStructures.map(struct => (
                      <TableRow key={struct.id} hover onClick={() => toggleStructureSelection(struct.id)} sx={{ cursor: 'pointer' }}>
                        <TableCell padding="checkbox">
                          <IconButton size="small">
                            {selectedStructures.includes(struct.id)
                              ? <CheckBoxIcon color="primary" />
                              : <CheckBoxOutlineBlankIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{struct.feeHead?.name || 'N/A'}</TableCell>
                        <TableCell align="right">₹{Number(struct.amount).toLocaleString()}</TableCell>
                        <TableCell>{struct.dueDate ? format(new Date(struct.dueDate), 'dd/MM/yyyy') : 'N/A'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignFees}
            disabled={selectedStructures.length === 0 || loading}
            startIcon={loading ? <CircularProgress size={16} /> : <AssignIcon />}
          >
            Assign {selectedStructures.length > 0 ? `(${selectedStructures.length})` : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Fees;
